import { useEffect } from 'react';
import Navbar from '../../../components/Navbar';
import Footer from '../../home/components/Footer';

const services = [
  {
    emoji: '🔧',
    name: 'Electrical Repairs',
    slug: 'electrical-repairs',
    description: 'Fast and reliable electrical repairs for all types of faults, from flickering lights to power outages in your Watford home.'
  },
  {
    emoji: '🔍',
    name: 'Fault Finding & Diagnostics',
    slug: 'fault-finding',
    description: 'Expert electrical fault finding using advanced diagnostic equipment to identify and resolve issues quickly and efficiently.'
  },
  {
    emoji: '⚠️',
    name: 'Tripping Circuits',
    slug: 'tripping-circuits',
    description: 'Diagnose and fix tripping circuit breakers and RCDs to restore safe and reliable power to your property.'
  },
  {
    emoji: '📦',
    name: 'Fuse Box Repairs',
    slug: 'fuse-box-repairs',
    description: 'Consumer unit and fuse box repairs to ensure your electrical system operates safely and meets current regulations.'
  },
  {
    emoji: '💡',
    name: 'Lighting Repairs',
    slug: 'lighting-repairs',
    description: 'Fix flickering lights, non-working switches, and all lighting problems throughout your home or business.'
  },
  {
    emoji: '🔌',
    name: 'Socket & Switch Repairs',
    slug: 'socket-switch-repairs',
    description: 'Repair or replace faulty sockets, switches, and outlets that are damaged, loose, or not working properly.'
  },
  {
    emoji: '🚨',
    name: 'Emergency Call-Outs',
    slug: 'emergency-callouts',
    description: '24/7 emergency electrical services for urgent repairs when you need an electrician immediately in Watford.'
  },
  {
    emoji: '📋',
    name: 'Electrical Maintenance Contracts',
    slug: 'maintenance-contracts',
    description: 'Scheduled maintenance plans to keep your electrical systems in top condition and prevent costly breakdowns.'
  },
  {
    emoji: '✅',
    name: 'PAT Testing',
    slug: 'pat-testing',
    description: 'Portable Appliance Testing to ensure your electrical equipment is safe and compliant with regulations.'
  },
  {
    emoji: '🔨',
    name: 'Electrical Appliance Repairs',
    slug: 'appliance-repairs',
    description: 'Professional repair services for electrical appliances including cookers, showers, and heating systems.'
  }
];

export default function RepairsPage() {
  useEffect(() => {
    document.title = 'Electrical Repairs & Maintenance Watford | Beyond Electrical Services';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Fast electrical repairs & maintenance in Watford. 24/7 emergency callouts, fault finding, fuse box repairs, PAT testing & more. NICEIC approved. Call +44 7989 675952.');
    }

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      (metaKeywords as HTMLMetaElement).name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    (metaKeywords as HTMLMetaElement).content = 'electrical repairs Watford, emergency electrician Watford, fault finding Watford, PAT testing Watford, fuse box repair Watford';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      (canonical as HTMLLinkElement).rel = 'canonical';
      document.head.appendChild(canonical);
    }
    (canonical as HTMLLinkElement).href = `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/repairs`;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'repairs-schema';
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Beyond Electrical Services",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/repairs`,
        "telephone": "+447989675952",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Watford",
          "addressRegion": "Hertfordshire",
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 51.6565,
          "longitude": -0.3957
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "priceRange": "££",
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
            "opens": "00:00",
            "closes": "23:59",
            "description": "24/7 Emergency Call-Outs"
          }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Electrical Repair & Maintenance Services Watford",
        "description": "Fast, reliable electrical repair and maintenance services for homes and businesses across Watford. 24/7 emergency call-outs available.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Beyond Electrical Services",
          "telephone": "+447989675952"
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "serviceType": "Electrical Repairs & Maintenance",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/repairs`
      }
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('repairs-schema');
      if (s) document.head.removeChild(s);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/2385c959203f00f06860adbd2a254abf.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              NICEIC Approved
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Serving Watford & Beyond
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-time-fill"></i>
              24/7 Emergency Service
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Repair & Maintenance<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Fast, reliable electrical repair and maintenance services for homes and businesses across Watford, Rickmansworth, and surrounding areas. From emergency call-outs to scheduled maintenance, our NICEIC certified electricians are here to help 24/7.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+447989675952"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              +44 7989 675952
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Get a Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Our Repair & Maintenance Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive electrical repair and maintenance solutions to keep your property safe and functional
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <a
                key={service.slug}
                href={`/services/repairs/${service.slug}`}
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-[#FFD700] cursor-pointer"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-xl text-3xl group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    {service.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-[#0A1628] mb-2 group-hover:text-[#FFD700] transition-colors duration-300">
                      {service.name}
                    </h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                <div className="flex items-center text-[#FFD700] font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Learn More
                  <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform duration-300"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Your trusted local electricians in Watford for all repair and maintenance needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                🚨
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">24/7 Emergency Service</h3>
              <p className="text-gray-300 leading-relaxed">
                Round-the-clock emergency call-outs for urgent electrical repairs when you need us most
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                ⚡
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Fast Response Times</h3>
              <p className="text-gray-300 leading-relaxed">
                Quick diagnosis and repair to minimize downtime and get your electrical systems working again
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                ✅
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">NICEIC Certified</h3>
              <p className="text-gray-300 leading-relaxed">
                Fully qualified electricians ensuring all repairs meet the highest safety standards and regulations
              </p>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">500+</div>
              <div className="text-gray-300 text-sm">Repairs Completed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">100%</div>
              <div className="text-gray-300 text-sm">Customer Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">24/7</div>
              <div className="text-gray-300 text-sm">Emergency Service</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">15+</div>
              <div className="text-gray-300 text-sm">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-6">
            Areas We Cover
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Providing professional electrical repair and maintenance services throughout Watford, Rickmansworth, and surrounding areas including:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Watford', 'Rickmansworth', 'Bushey', 'Croxley Green', 'Abbots Langley', 'Kings Langley', 'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Borehamwood', 'Radlett'].map((area) => (
              <span
                key={area}
                className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-5 py-2.5 rounded-full font-semibold text-sm shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Need an Electrical Repair?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Get in touch today for fast, reliable electrical repairs in Watford. Our expert team is available 24/7 for emergency call-outs and scheduled maintenance.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+447989675952"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call +44 7989 675952
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Request a Quote
            </a>
          </div>
          <div className="mt-8 flex items-center justify-center gap-2 text-[#FFD700] text-sm font-semibold">
            <i className="ri-flashlight-fill"></i>
            24/7 Emergency Callout Available
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}